import { Injectable } from "@angular/core";
import * as ExcelJS from "exceljs";
import * as FileSaver from "file-saver";

@Injectable({
  providedIn: "root",
})
export class ExcelService {
  constructor() {}

  async generateEvaluationSheet(data?: any | any[]) {
    // =============================
    // Workbook & Worksheet
    // =============================
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Evaluation Sheet");

    // =============================
    // Column setup
    // =============================
    worksheet.columns = [
      { key: "col1", width: 30 },
      { key: "col2", width: 20 },
      { key: "col3", width: 20 },
      { key: "col4", width: 20 },
      { key: "col5", width: 20 },
    ];

    // =============================
    // Common Styles (STRICT SAFE)
    // =============================
    const centerAlignment: Partial<ExcelJS.Alignment> = {
      vertical: "middle",
      horizontal: "center",
    };

    const boldFont: Partial<ExcelJS.Font> = { bold: true };

    const borderStyle: Partial<ExcelJS.Borders> = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    const headerFill: ExcelJS.Fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFE599" },
    };

    // =============================
    // HEADER
    // =============================
    worksheet.mergeCells("A1:E1");
    worksheet.getCell("A1").value = "SPAARTAN";
    worksheet.getCell("A1").font = { bold: true, size: 24, color: { argb: "FFFF0000" } };
    worksheet.getCell("A1").alignment = centerAlignment;

    worksheet.mergeCells("A2:E2");
    worksheet.getCell("A2").value = "VISPY'S MARTIAL ARTS ACADEMY";
    worksheet.getCell("A2").font = { bold: true, size: 16, color: { argb: "FFFF0000" } };
    worksheet.getCell("A2").alignment = centerAlignment;

    const affiliation = ["AFFILIATED TO NIPPON BUDO SOGO INTERNATIONAL INDIA", "MEMBER OF KUDO FEDERATION GUJARAT", "MEMBER OF KUDO INTERNATIONAL FEDERATION INDIA"];

    affiliation.forEach((text, i) => {
      const row = i + 3;
      worksheet.mergeCells(`A${row}:E${row}`);
      worksheet.getCell(`A${row}`).value = text;
      worksheet.getCell(`A${row}`).alignment = centerAlignment;
      worksheet.getCell(`A${row}`).font = { size: 9 };
    });

    // =============================
    // PREPARE DATA
    // =============================
    let performanceRecords: any[] = [];
    let headerData: any = {};

    if (Array.isArray(data)) {
      performanceRecords = data;
      if (performanceRecords.length > 0) {
        headerData = performanceRecords[0];
      }
    } else if (data) {
      performanceRecords = [data];
      headerData = data;
    }

    // =============================
    // STUDENT DETAILS
    // =============================
    worksheet.addRow([]);
    worksheet.mergeCells("A7:E7");
    const studentInfo = headerData.FirstName
      ? `Name: ${headerData.FirstName} ${headerData.MiddleName} ${headerData.LastName} | Height: ${headerData.Height} cm | Weight: ${headerData.Weight} kg | BMI: ${headerData.BMI}`
      : "Name of the Student and Details (Roll No etc)";
    worksheet.getCell("A7").value = studentInfo;
    worksheet.getCell("A7").font = boldFont;
    worksheet.getCell("A7").alignment = centerAlignment;
    worksheet.getCell("A7").border = borderStyle;
    worksheet.getRow(7).height = 25;

    // =============================
    // TABLE HEADERS
    // =============================
    let rowIndex = 8;

    worksheet.mergeCells(`B${rowIndex}:E${rowIndex}`);
    const year = headerData.EvaluationYear ? new Date(headerData.EvaluationYear).getFullYear() : new Date().getFullYear();
    worksheet.getCell(`B${rowIndex}`).value = `Performance Evaluation Sheet - for the year ${year}`;
    worksheet.getCell(`B${rowIndex}`).font = boldFont;
    worksheet.getCell(`B${rowIndex}`).alignment = centerAlignment;
    worksheet.getCell(`B${rowIndex}`).fill = headerFill;

    worksheet.mergeCells(`A8:A${rowIndex}`);
    worksheet.getCell("A8").value = headerData.CenterName ? `Centre: ${headerData.CenterName}` : "Centre:";
    worksheet.getCell("A8").font = boldFont;
    worksheet.getCell("A8").alignment = centerAlignment;
    worksheet.getCell("A8").fill = headerFill;

    rowIndex++;
    const months = ["", "January - March", "April - June", "July - September", "October - December"];
    worksheet.getRow(rowIndex).values = months;
    worksheet.getRow(rowIndex).eachCell((cell, col) => {
      if (col > 1) {
        cell.fill = headerFill;
        cell.font = boldFont;
        cell.alignment = centerAlignment;
      }
    });

    rowIndex++;
    const quarters = ["", "Quarter - 1", "Quarter - 2", "Quarter - 3", "Quarter - 4"];
    worksheet.getRow(rowIndex).values = quarters;
    worksheet.getRow(rowIndex).eachCell((cell, col) => {
      if (col > 1) {
        cell.fill = headerFill;
        cell.font = boldFont;
        cell.alignment = centerAlignment;
      }
    });

    rowIndex++;
    worksheet.mergeCells(`B${rowIndex}:E${rowIndex}`);
    worksheet.getCell(`B${rowIndex}`).value = "Ratings (Excellent, Very Good, Good, Satisfactory) A to D";
    worksheet.getCell(`B${rowIndex}`).font = boldFont;
    worksheet.getCell(`B${rowIndex}`).alignment = centerAlignment;
    worksheet.getCell(`B${rowIndex}`).fill = headerFill;

    // =============================
    // CRITERIA
    // =============================
    // rowIndex++;
    // worksheet.getCell(`A${rowIndex}`).value = "Performance Criteria";
    // worksheet.getCell(`A${rowIndex}`).font = boldFont;
    // worksheet.getCell(`A${rowIndex}`).alignment = centerAlignment;
    // worksheet.getCell(`A${rowIndex}`).fill = headerFill;

    const criteriaMap: { [key: string]: string } = {
      "Performance Criteria": "Remarks",
      Attendance: "Attendance",
      Cleanliness: "Cleanliness",
      Discipline: "Discipline",
      Grasping: "Grasping",
      Conditioning: "Conditioning",
      Basics: "Basics",
      Punches: "Punches",
      Kicks: "Kicks",
      Combination: "Combination",
      Throws: "Throws",
      Falls: "Falls",
      Rolls: "Rolls",
      Kata: "Kata",
      Kumite: "Kumite",
      "Scope of Improvement": "ScopeofImprovement",
      Strength: "Strength",
    };

    const criteria = Object.keys(criteriaMap);

    // Map criteria to row indices
    const criteriaRows: { [key: string]: number } = {};
    criteria.forEach(text => {
      rowIndex++;
      worksheet.getCell(`A${rowIndex}`).value = text;
      worksheet.getCell(`A${rowIndex}`).font = boldFont;
      worksheet.getCell(`A${rowIndex}`).alignment = centerAlignment;
      worksheet.getCell(`A${rowIndex}`).fill = headerFill;
      criteriaRows[text] = rowIndex;
    });

    // Fill data for each record
    performanceRecords.forEach(record => {
      const targetCol = (record.QuarterNumber || 1) + 1; // Quarter 1 -> Col 2, etc.

      criteria.forEach(text => {
        const dataKey = criteriaMap[text];
        const row = criteriaRows[text];
        if (record[dataKey] !== undefined) {
          worksheet.getRow(row).getCell(targetCol).value = record[dataKey];
          worksheet.getRow(row).getCell(targetCol).alignment = centerAlignment;
        }
      });
    });

    for (let r = 8; r <= rowIndex; r++) {
      for (let c = 1; c <= 5; c++) {
        worksheet.getRow(r).getCell(c).border = borderStyle;
      }
    }

    // =============================
    // FOOTER
    // =============================
    rowIndex += 2; // space before footer

    // Merge footer rows
    worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);
    worksheet.getCell(`A${rowIndex}`).value = "CORRESPONDENCE ADDRESS : NAVAL BUNGLOW, OPP ELECTRIC SUB STATION, Nr. ADARSH SOCIETY, ATHWALINES, SURAT 395001";
    worksheet.getCell(`A${rowIndex}`).alignment = centerAlignment;
    worksheet.getCell(`A${rowIndex}`).font = { size: 8 };
    worksheet.getCell(`A${rowIndex}`).style = { font: boldFont };

    worksheet.getCell(`A${rowIndex}`).border = {
      top: { style: "thin" },
    };

    rowIndex++;

    worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);
    worksheet.getCell(`A${rowIndex}`).value = "Email Address : vispykharadi@gmail.com     Contact Number : +91-98255 00048     Website : WWW.SPAARTAN.COM";
    worksheet.getCell(`A${rowIndex}`).alignment = centerAlignment;
    worksheet.getCell(`A${rowIndex}`).font = { size: 9 };
    worksheet.getCell(`A${rowIndex}`).border = {
      bottom: { style: "thin" },
    };

    // Adjust height
    worksheet.getRow(rowIndex - 1).height = 18;
    worksheet.getRow(rowIndex).height = 18;

    // =============================
    // DOWNLOAD
    // =============================
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = headerData.FirstName ? `Spaartan_Evaluation_Sheet_${headerData.FirstName}.xlsx` : "Spaartan_Evaluation_Sheet.xlsx";
    FileSaver.saveAs(blob, fileName);
  }

  async getBase64FromUrl(imageUrl: string): Promise<string> {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
